/*
 * Purpose: Work Engineer AI — diagnose errors, plan fixes, queue push
 * Last modified: 2026-06-28 | Agent
 * Completeness: 94
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Rocket, Wrench } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ContextualError } from '@/components/errors/ContextualError';
import {
  createEngineerJob,
  diagnoseWithEngineer,
  listEngineerJobs,
  pushEngineerJob,
} from '@/lib/adminApi';
import { fetchSystemHealth } from '@/lib/systemHealthApi';
import { useLogStore } from '@/stores/logStore';
import type { EngineerDiagnosis, EngineerJob } from '@/lib/siteAnalyticsTypes';
import { APP_ROUTES } from '@/lib/appRoutes';
import { toast } from 'sonner';

export function WorkEngineerPanel() {
  const logs = useLogStore((s) => s.logs);
  const [searchParams] = useSearchParams();
  const [diagnosis, setDiagnosis] = useState<EngineerDiagnosis | null>(null);
  const [jobs, setJobs] = useState<EngineerJob[]>([]);
  const [activeJob, setActiveJob] = useState<EngineerJob | null>(null);
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorAction, setErrorAction] = useState<{ label: string; href: string } | undefined>();

  const loadJobs = useCallback(async () => {
    try {
      setJobs(await listEngineerJobs());
    } catch {
      /* optional on mock */
    }
  }, []);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    const jobId = searchParams.get('job');
    if (jobId) {
      const found = jobs.find((j) => j.id === jobId);
      if (found) setActiveJob(found);
    }
  }, [searchParams, jobs]);

  const diagnose = async () => {
    setLoading(true);
    setError(null);
    setErrorAction(undefined);
    try {
      const health = await fetchSystemHealth().catch(() => null);
      const errorLogs = logs
        .filter((l) => l.level === 'error' || l.level === 'warning')
        .slice(0, 30)
        .map((l) => ({
          level: l.level,
          message: l.message,
          timestamp: l.timestamp.toISOString(),
        }));

      const result = await diagnoseWithEngineer({
        errors: errorLogs.map((l) => ({ message: l.message, timestamp: l.timestamp })),
        health: health?.data ?? null,
        logs: errorLogs,
      });
      setDiagnosis(result);
    } catch (e) {
      const err = e as Error & { action?: { label: string; href: string } };
      setError(err.message);
      setErrorAction(err.action);
    } finally {
      setLoading(false);
    }
  };

  const queueFix = async (dryRun: boolean) => {
    if (!diagnosis) return;
    setPushing(true);
    try {
      const job = await createEngineerJob(diagnosis, dryRun);
      toast.success(dryRun ? 'Fix plan saved (dry run)' : 'Fix job queued');
      setActiveJob(job);
      await loadJobs();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to queue job');
    } finally {
      setPushing(false);
    }
  };

  const pushJob = async (jobId: string) => {
    setPushing(true);
    try {
      const result = await pushEngineerJob(jobId);
      toast.success(result.message);
      setActiveJob(result.job);
      await loadJobs();
    } catch (e) {
      const err = e as Error & { action?: { label: string; href: string } };
      toast.error(err.message);
    } finally {
      setPushing(false);
    }
  };

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <Wrench className="h-6 w-6 text-[hsl(var(--ab-green-core))]" />
            Work Engineer AI
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Diagnoses runtime errors, proposes fixes, and queues deployments when{' '}
            <code className="text-xs bg-muted px-1 rounded">ENGINEER_AUTO_PUSH=1</code> is set.
          </p>
        </div>
        <Button onClick={() => void diagnose()} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
          Diagnose now
        </Button>
      </div>

      {error && (
        <ContextualError
          message={error}
          action={
            errorAction
              ? { label: errorAction.label, href: errorAction.href }
              : { label: 'Retry', onClick: () => void diagnose() }
          }
          secondaryAction={{ label: 'System status', href: APP_ROUTES.systemStatus }}
        />
      )}

      {diagnosis && (
        <Card>
          <CardHeader>
            <CardTitle>{diagnosis.headline}</CardTitle>
            <CardDescription>{diagnosis.deployNotes}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {diagnosis.rootCauses.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1">Root causes</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {diagnosis.rootCauses.map((c, i) => (
                    <li key={i}>• {c}</li>
                  ))}
                </ul>
              </div>
            )}

            {diagnosis.fixes.map((fix, i) => (
              <div key={i} className="rounded-lg border border-border/60 p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{fix.title}</span>
                  <Badge variant="outline">{fix.risk} risk</Badge>
                </div>
                {fix.files.length > 0 && (
                  <p className="text-xs font-mono text-muted-foreground">{fix.files.join(', ')}</p>
                )}
                <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-0.5">
                  {fix.steps.map((s, j) => (
                    <li key={j}>{s}</li>
                  ))}
                </ol>
              </div>
            ))}

            <div className="flex flex-wrap gap-2 pt-2">
              <Button size="sm" variant="outline" disabled={pushing} onClick={() => void queueFix(true)}>
                Save fix plan
              </Button>
              {diagnosis.canAutoPush && (
                <Button size="sm" disabled={pushing} onClick={() => void queueFix(false)}>
                  <Rocket className="h-4 w-4 mr-1.5" />
                  Queue push
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {activeJob && (
        <Card className="border-[hsl(var(--ab-green-core)/0.3)]">
          <CardHeader>
            <CardTitle className="text-base">Active job · {activeJob.id}</CardTitle>
            <CardDescription>
              Status: {activeJob.status} · {activeJob.dryRun ? 'dry run' : 'live'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {activeJob.log.map((line, i) => (
              <p key={i} className="text-xs text-muted-foreground font-mono">
                {line}
              </p>
            ))}
            {!activeJob.dryRun && activeJob.status === 'planned' && (
              <Button size="sm" disabled={pushing} onClick={() => void pushJob(activeJob.id)}>
                Push update
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {jobs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent jobs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {jobs.map((j) => (
              <button
                key={j.id}
                type="button"
                className="w-full text-left text-sm flex items-center justify-between gap-2 rounded-md border border-border/50 px-3 py-2 hover:bg-muted/40"
                onClick={() => setActiveJob(j)}
              >
                <span className="font-mono text-xs">{j.id}</span>
                <Badge variant="secondary">{j.status}</Badge>
              </button>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
